/* eslint-disable react/no-array-index-key */
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type { ValidCharacterItem } from "../types/api-types";
import ExportCsvButton from "../../shared/export/export-csv-button";
import type { CsvColumn } from "../../shared/export/csv-export";

const exportColumns: CsvColumn<ValidCharacterItem>[] = [
  { header: "Description", value: (character) => character?.description ?? "" },
];

interface ValidCharactersTableProps {
  characters: ValidCharacterItem[];
  isLoading: boolean;
  onRemoveCharacter: (character: ValidCharacterItem) => void;
}

const ValidCharactersTable = ({
  characters,
  isLoading,
  onRemoveCharacter,
}: ValidCharactersTableProps) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Loading valid characters...
        </Typography>
      </Box>
    );
  }

  if (characters.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No active characters configured
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      sx={{
        maxHeight: 300,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          display: "none",
        },
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              "& .MuiTableCell-root": {
                fontWeight: 600,
                color: "primary.main",
                bgcolor: "grey.50",
              },
            }}
          >
            <TableCell>Description</TableCell>
            <TableCell align="right">
              <ExportCsvButton
                rows={characters}
                columns={exportColumns}
                fileName="valid-characters"
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {characters.map((character, index) => (
            <TableRow
              key={`character-${index}`}
              sx={{
                "&:hover": {
                  bgcolor: "action.hover",
                },
                "&:last-child td": {
                  borderBottom: 0,
                },
              }}
            >
              <TableCell sx={{ py: 1.5 }}>
                {character?.description || ""}
              </TableCell>
              <TableCell align="right" sx={{ py: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => onRemoveCharacter(character)}
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      color: "error.main",
                      bgcolor: "error.lighter",
                    },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ValidCharactersTable;
